// src/components/OpportunityForm.tsx
import React, { useState, useEffect, useRef, useCallback } from 'react';
import Select from 'react-select';
import DatePicker, { registerLocale } from 'react-datepicker';
import { ptBR } from 'date-fns/locale/pt-BR';
import axios from 'axios';
import { Funnel, Stage } from '../../../types/funnels';
import { apiService } from '../../../services/api';
import { CreateOpportunityData, OpportunityData, OpportunityStatus, TempoEntrega } from '../../../types/opportunity';
import { Produto } from '../../../types/produtos';
import { ProdutosOportunidadeInput, ProdutoOportunidade } from '../../../types/orcamento';
import { Contato } from '../../../types/contato';
import { CustomField } from '../../../types/camposPersonalizados';
import ModalConfirmacaoContrato from './components/ModalConfirmacaoContrato';
import QuickContactForm from './components/QuickContactForm';
import QuickOriginForm from './components/QuickOriginForm';
import QuickProductForm from './components/QuickProductForm';
import QuickStatusForm from './components/QuickStatusForm';
import QuickUnitForm from './components/QuickUnitForm';
import { useUnidadesMedida } from '../../../hooks/useUnidadesMedida';
import { toast } from "react-toastify";
// Corrigindo a importação do locale e capitalizando os meses
const MONTHS = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro'
];

const MONTHS_SHORT = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez'
];

const customPtBR = {
  ...ptBR,
  localize: {
    ...ptBR.localize,
    month: (n: number) => MONTHS[n],
    monthShort: (n: number) => MONTHS_SHORT[n]
  }
};

registerLocale('pt-BR', customPtBR as any);

// Interface para as opções dos selects
interface SelectOption {
  value: string | number;
  label: string;
}

interface ValoresCustomFields {
  [key: string]: any;
}

// Interface para os dados do formulário
interface IFormData {
  numero_contrato: string; // Novo campo como string porque vem do input
  title: string;
  source: SelectOption | null;
  funnel: SelectOption | null;
  funnelStage: SelectOption | null;
  owner: SelectOption | null;
  contact: SelectOption | null;
  municipality: SelectOption | null;
  bidValue: string;
  status: SelectOption | null;
  produtos: ProdutosOportunidadeInput[];
  url_site_externo: string;
  uasb: string;
  tempo_entrega: SelectOption | null;
  contatoUf: string; // Novo campo para armazenar UF do contato
}

export interface OpportunityFormProps {
  funnels: Funnel[];
  selectedFunnel: Funnel | null;
  setSelectedFunnel: (funnel: Funnel) => void;
  usersList: SelectOption[];
  setOpenModal: (open: boolean) => void;
  modalType?: "create" | "edit";
  isEditing?: boolean;
  initialData?: OpportunityData;
  onClose?: () => void;
  onSubmit: (data: CreateOpportunityData) => void;
  onOpportunityCreated?: (opportunity: any) => void;
  closeOnSuccess?: boolean;
}

const tempoEntregaOptions: { value: TempoEntrega; label: string; }[] = [
  { value: "Única", label: "Única" },
  { value: "Fracionada", label: "Fracionada" },
  { value: "Sem informação", label: "Sem informação" }
];

export interface OpportunityFormData extends IFormData {
  bidDateTime: Date | null;
}

const OpportunityForm: React.FC<OpportunityFormProps> = ({
  funnels,
  selectedFunnel,
  setSelectedFunnel,
  usersList,
  setOpenModal,
  isEditing = false, // Adicione um valor padrão
  initialData,
  onClose,
  onSubmit,
  onOpportunityCreated,
  closeOnSuccess
}) => {
  // Estados
  const [bidDateTime, setBidDateTime] = useState<Date | null>(new Date());
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [contacts, setContacts] = useState<SelectOption[]>([]);
  const [sourceOptions, setSourceOptions] = useState<SelectOption[]>([]);
  const [statusList, setStatusList] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [municipalities, setMunicipalities] = useState<SelectOption[]>([]);
  const [valoresExistentes, setValoresExistentes] = useState<ValoresCustomFields>({});
  const [produtos, setProdutos] = useState<Produto[]>([]);
  const [produtosOportunidade, setProdutosOportunidade] = useState<ProdutosOportunidadeInput[]>([]);
  const [loadingProdutos, setLoadingProdutos] = useState(false);
  // Estado intermediário para o texto digitado no campo Valor Ref. (permite vírgula durante digitação)
  const [valoresRefTexto, setValoresRefTexto] = useState<Record<number, string>>({});
  const formTopRef = useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState<IFormData>({
    numero_contrato: '', // Valor inicial vazio
    title: '',
    source: null,
    funnel: null,
    funnelStage: null,
    owner: null,
    contact: null,
    municipality: null,
    bidValue: '',
    status: null,
    produtos: [],
    url_site_externo: '',
    uasb: '',
    tempo_entrega: null,
    contatoUf: '' // Valor inicial vazio para UF do contato
  });

  const [camposPersonalizados, setCamposPersonalizados] = useState<CustomField[]>([]);
  const [valoresCamposPersonalizados, setValoresCamposPersonalizados] = useState<{ [key: string]: any }>({});

  // Novos estados para o modal de criação de contrato
  const [showModalContrato, setShowModalContrato] = useState<boolean>(false);
  const [statusAnterior, setStatusAnterior] = useState<SelectOption | null>(null);
  const [criarContrato, setCriarContrato] = useState<boolean>(false);
  const [datasContrato, setDatasContrato] = useState<{ data_inicio: string; data_fim: string; prazo_vencimento: number } | null>(null);

  // Estados para controlar os formulários inline de cadastro rápido
  const { unidadesMedida, refetchUnidadesMedida } = useUnidadesMedida();
  const [showQuickContactForm, setShowQuickContactForm] = useState<boolean>(false);
  const [showQuickOriginForm, setShowQuickOriginForm] = useState<boolean>(false);
  const [showQuickProductForm, setShowQuickProductForm] = useState<boolean>(false);
  const [showQuickStatusForm, setShowQuickStatusForm] = useState<boolean>(false);
  const [showQuickUnitForm, setShowQuickUnitForm] = useState<boolean>(false);

  // Função auxiliar para tratar erros
  const getErrorMessage = (error: unknown): string => {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        return error.response.data?.message || 'Erro no servidor';
      }
      if (!error.response) {
        return 'Não foi possível conectar ao servidor';
      }
    }
    if (error instanceof Error) return error.message;
    return 'Ocorreu um erro inesperado';
  };

  const podeGerarContrato = () => {
    if (!selectedFunnel || !formData.funnelStage) {
      return false;
    }

    const etapas = selectedFunnel.etapas || [];
    if (etapas.length === 0) return false;

    const etapaAtual = etapas.find(etapa => etapa.id === Number(formData.funnelStage!.value));
    return etapaAtual?.gera_contrato === true;
  };

  const formatCurrency = (value: number | string): string => {
    const numValue = typeof value === 'string' ? parseFloat(value) : value;
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(numValue || 0);
  };

  const formatNumberForInput = (value: number): string => {
    return value.toString().replace('.', ',');
  };

  useEffect(() => {
    console.log('Debug - Edição:', {
      isEditing,
      initialData,
      formDataAtual: formData
    });

    // Add this new console.log block for produtos
    console.log('Debug - Produtos na edição:', {
      id_oportunidade: initialData?.id,
      produtos_atuais: produtosOportunidade,
      produtos_no_formData: formData.produtos
    });

    // Log específico para o campo uasb
    console.log('Debug - Campo UASB:', {
      valor_no_initialData: initialData?.uasb,
      tipo: initialData?.uasb !== undefined ? typeof initialData.uasb : 'undefined'
    });
    if (isEditing && initialData) {

      console.log('Debug - Campos problemáticos:', {
        numero_contrato: initialData.numero_contrato,
        id_origem: initialData.id_origem,
        nome_origem: initialData.nome_origem
      });

      setFormData({
        numero_contrato: initialData.numero_contrato,
        title: initialData.titulo,
        source: {
          value: initialData.id_origem,
          label: initialData.nome_origem
        },
        funnel: {
          value: initialData.id_funil,
          label: initialData.nome_funil
        },
        funnelStage: {
          value: initialData.id_etapa,
          label: initialData.nome_etapa
        },
        owner: {
          value: initialData.id_dono,
          label: initialData.nome_dono
        },
        contact: {
          value: initialData.id_contato,
          label: initialData.nome_contato
        },
        municipality: {
          value: initialData.id_municipio,
          label: initialData.nome_municipio
        },
        bidValue: new Intl.NumberFormat('pt-BR', {
          style: 'currency',
          currency: 'BRL'
        }).format(initialData.valor_licitacao),
        status: initialData.id_status ? {
          value: initialData.id_status,
          label: initialData.status
        } : {
          value: initialData.status,
          label: initialData.status
        },
        produtos: [],
        url_site_externo: initialData.url_site_externo || '',
        uasb: initialData.uasb === undefined ? '' : initialData.uasb,
        tempo_entrega: initialData.tempo_entrega ? {
          value: initialData.tempo_entrega,
          label: initialData.tempo_entrega
        } : { value: 'Sem informação', label: 'Sem informação' },
        contatoUf: initialData.municipio_uf || '' // Carregar UF do contato na edição
      });

      if (initialData.data_licitacao) {

        // SOLUÇÃO: Garantir que a data seja tratada corretamente como data local
        // Se vier no formato "YYYY-MM-DD HH:MM:SS"
        if (typeof initialData.data_licitacao === 'string' && initialData.data_licitacao.includes(' ')) {
          // Converter para o formato que o JavaScript entende (substituir espaço por T)
          const dataFormatada = initialData.data_licitacao.replace(' ', 'T');
          setBidDateTime(new Date(dataFormatada));

          console.log('Debug - Data reformatada:', {
            original: initialData.data_licitacao,
            formatada: dataFormatada,
            objeto: new Date(dataFormatada)
          });
        } else {
          // Caso já venha em outro formato (ISO ou objeto Date)
          setBidDateTime(new Date(initialData.data_licitacao));
        }
      }
    }
  }, [isEditing, initialData]);


  useEffect(() => {
    const loadInitialData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Verificar se tem token antes de fazer as requisições
        const token = localStorage.getItem('token');
        if (!token) {
          throw new Error('Token de autenticação não encontrado. Por favor, faça login novamente.');
        }

        // Apenas define o dono automaticamente se NÃO estiver em modo de edição
        if (!isEditing) {
          const userInfo = getUserInfo();
          const ownerOption: SelectOption = {
            value: userInfo.id,
            label: userInfo.name
          };

          // Definir automaticamente o funil selecionado no kanban
          const funnelOption: SelectOption | null = selectedFunnel ? {
            value: selectedFunnel.id,
            label: selectedFunnel.nome
          } : null;

          let initialStage: SelectOption | null = null;
          if (selectedFunnel && selectedFunnel.etapas && selectedFunnel.etapas.length > 0) {
            // Ordenar as etapas por ordem (se tiver) e pegar a primeira
            const sortedStages = [...selectedFunnel.etapas].sort((a, b) => {
              const ordemA = typeof a.ordem === 'number' ? a.ordem : 0;
              const ordemB = typeof b.ordem === 'number' ? b.ordem : 0;
              return ordemA - ordemB;
            });

            const firstStage = sortedStages[0];
            // Verificar se firstStage existe e se id existe
            if (firstStage && firstStage.id !== undefined) {
              initialStage = {
                value: firstStage.id, // Agora sabemos que id não é undefined
                label: firstStage.nome || 'Sem nome' // Adicionar fallback para nome também
              };
            }
          }

          setFormData(prev => ({
            ...prev,
            owner: ownerOption,
            funnel: funnelOption, // Define o funil automaticamente
            funnelStage: initialStage // Define a etapa inicial automaticamente
          }));
        }

        // Fazer todas as chamadas em paralelo
        const [contatosResponse, origensResponse, statusResponse] = await Promise.all([
          apiService.getContatos(),
          apiService.getOrigens(),
          apiService.getStatusOportunidades()
        ]);

        if (!contatosResponse.success || !origensResponse.success || !statusResponse.success) {
          throw new Error('Erro ao carregar dados. Verifique sua conexão e tente novamente.');
        }

        // Converter os dados para o formato do Select
        setContacts(
          contatosResponse.data.map(contato => ({
            value: contato.id,
            label: contato.tipo_entidade === 'PJ'
              ? (contato.razao_social || contato.nome_fantasia || contato.nome || 'Sem identificação')
              : (contato.nome || 'Sem nome')
          }))
        );

        setSourceOptions(
          origensResponse.data.map(origem => ({
            value: origem.id,
            label: origem.nome
          }))
        );

        setStatusList(
          statusResponse.data.map((status: any) => ({
            value: status.id,
            label: status.nome,
            e_final: status.e_final === "1" || status.e_final === 1 || status.e_final === true
          }))
        );

      } catch (error) {
        let errorMessage = 'Erro ao carregar dados';

        if (error instanceof Error) {
          errorMessage = error.message;
        }
        if (axios.isAxiosError(error) && error.response?.status === 401) {
          errorMessage = 'Sessão expirada. Por favor, faça login novamente.';
        }

        setError(errorMessage);
        console.error('Erro ao carregar dados:', error);
      } finally {
        setLoading(false);
      }
    };

    loadInitialData();
  }, [selectedFunnel, isEditing]);

  useEffect(() => {
    const carregarCamposPersonalizados = async () => {
      if (formData.funnel && formData.funnelStage) {
        try {
          console.log('Carregando campos personalizados para funil:', formData.funnel.value, 'etapa:', formData.funnelStage.value);

          const respostaCampos = await apiService.getCustomFields(
            Number(formData.funnel.value),
            Number(formData.funnelStage.value)
          );

          console.log('Resposta de campos personalizados:', respostaCampos);

          // Antes: filtrava só os campos com exibir_criacao = true
          // Modificação: usar todos os campos, independente de exibir_criacao
          const todosOsCampos = respostaCampos.data;

          // Para a interface, continuamos mostrando apenas os que têm exibir_criacao = true
          const camposAtivos = respostaCampos.data.filter(campo => campo.exibir_criacao);

          console.log('Campos ativos para exibição:', camposAtivos);
          console.log('Total de campos para salvar:', todosOsCampos.length);

          setCamposPersonalizados(camposAtivos);

          if (isEditing && initialData?.id) {
            console.log('Carregando valores existentes para ID:', initialData.id);
            const respostaValores = await apiService.getFieldValues(Number(initialData.id));

            console.log('Resposta valores:', respostaValores);

            if (respostaValores.success && respostaValores.data) {
              const valoresDoDb: ValoresCustomFields = respostaValores.data.reduce((acc, item) => ({
                ...acc,
                [item.id_campo_personalizado.toString()]: item.valor
              }), {} as ValoresCustomFields);

              console.log('Valores do DB formatados:', valoresDoDb);
              setValoresExistentes(valoresDoDb);

              // Inicializar valores para todos os campos, não só os exibidos
              const valoresIniciais = todosOsCampos.reduce((acc, campo) => {
                const valorExistente = valoresDoDb[campo.id.toString()];
                console.log(`Campo ${campo.id} (${campo.tipo_campo}):`, valorExistente);

                let valor = valorExistente;

                if (campo.tipo_campo === 'checkbox') {
                  valor = valorExistente === "1" || valorExistente === true;
                  console.log(`Convertendo checkbox ${campo.id}:`, valorExistente, ' -> ', valor);
                }
                else if (campo.tipo_campo === 'select' || campo.tipo_campo === 'multiselect') {
                  if (campo.tipo_campo === 'multiselect' && typeof valorExistente === 'string') {
                    valor = valorExistente.split(',').map(v => v.trim());
                  }
                }

                return {
                  ...acc,
                  [campo.id.toString()]: valor !== undefined ? valor : campo.valor_padrao || ''
                };
              }, {} as ValoresCustomFields);

              console.log('Valores iniciais formatados:', valoresIniciais);
              setValoresCamposPersonalizados(valoresIniciais);
            }
          } else {
            // Inicializar valores padrão para TODOS os campos no modo de criação
            const valoresIniciais = todosOsCampos.reduce((acc, campo) => {
              let valorPadrao;

              // Definir valor padrão conforme o tipo
              switch (campo.tipo_campo) {
                case 'checkbox':
                  valorPadrao = campo.valor_padrao === "1" ? true : false;
                  break;
                case 'numero':
                case 'monetario':
                case 'porcentagem':
                  valorPadrao = campo.valor_padrao || "0";
                  break;
                case 'select':
                case 'multiselect':
                case 'radio':
                  // Se tiver valor padrão definido, usar
                  if (campo.valor_padrao) {
                    valorPadrao = campo.valor_padrao;
                  }
                  // Senão, tentar pegar a primeira opção, se existir
                  else {
                    try {
                      if (campo.opcoes && typeof campo.opcoes === 'string') {
                        const opcoesParsed = JSON.parse(campo.opcoes);
                        if (Array.isArray(opcoesParsed) && opcoesParsed.length > 0) {
                          valorPadrao = opcoesParsed[0].id.toString();
                        } else {
                          valorPadrao = "";
                        }
                      } else if (campo.opcoes && Array.isArray(campo.opcoes) && campo.opcoes.length > 0) {
                        valorPadrao = campo.opcoes[0].id.toString();
                      } else {
                        valorPadrao = "";
                      }
                    } catch (err) {
                      console.error('Erro ao processar opções do campo:', err);
                      valorPadrao = "";
                    }
                  }
                  break;
                case 'data':
                case 'hora':
                  valorPadrao = campo.valor_padrao || null;
                  break;
                default:
                  valorPadrao = campo.valor_padrao || "";
              }

              console.log(`Definindo valor padrão para campo ${campo.id} (${campo.tipo_campo}):`, valorPadrao);

              return {
                ...acc,
                [campo.id.toString()]: valorPadrao
              };
            }, {} as ValoresCustomFields);

            console.log('Valores iniciais para todos os campos:', valoresIniciais);
            setValoresCamposPersonalizados(valoresIniciais);
          }
        } catch (error) {
          console.error('Erro ao carregar campos personalizados:', error);
          setError('Erro ao carregar campos personalizados');
        }
      } else {
        setCamposPersonalizados([]);
        setValoresCamposPersonalizados({});
      }
    };

    carregarCamposPersonalizados();
  }, [formData.funnel, formData.funnelStage, isEditing, initialData?.id]);

  // Dentro do useEffect que carrega produtos (linha aproximada 434)
  useEffect(() => {
    const carregarProdutos = async () => {
      try {
        setLoadingProdutos(true);

        // Carregar lista de produtos
        const produtosRes = await apiService.getProdutos();
        if (produtosRes.success && produtosRes.data) {
          console.log('Debug - Lista de produtos carregada:', produtosRes.data.length);
          setProdutos(produtosRes.data);
        }

        // Se estiver editando, carregar produtos da oportunidade
        if (isEditing && initialData?.id) {
          console.log('Debug - Carregando produtos da oportunidade ID:', initialData.id);
          const produtosOportunidadeRes = await apiService.getProdutosOportunidade(Number(initialData.id));

          console.log('Debug - Resposta da API produtos oportunidade:', produtosOportunidadeRes);
          if (produtosOportunidadeRes.success && produtosOportunidadeRes.data) {
            // Mapear produtos da oportunidade para o formato do formulário
            const produtosFormatados = produtosOportunidadeRes.data.map((po): ProdutosOportunidadeInput => {
              // Encontrar o produto correspondente na lista de produtos
              const produtoInfo = produtosRes.data.find(p => p.id === po.id_produto);

              console.log('Debug - Processando produto oportunidade:', {
                id_produto: po.id_produto,
                unidade_po: po.unidade,
                produto_encontrado: !!produtoInfo,
                unidade_produto: produtoInfo?.unidade_medida
              });

              // Lógica para determinar a unidade:
              // 1. Priorizar unidade_medida da resposta da API (campo correto dos produtos oportunidade)
              // 2. Se não tiver, usar unidade (campo alternativo)
              // 3. Se não tiver, usar unidade do produto
              // 4. Fallback para 'unidade'
              let unidadeUsar = 'unidade'; // fallback padrão

              if (po.unidade_medida && po.unidade_medida !== null) {
                // Usar unidade_medida da resposta da API (campo correto)
                unidadeUsar = po.unidade_medida;
                console.log('Debug - Usando unidade_medida da API:', unidadeUsar);
              } else if (po.unidade && po.unidade !== null) {
                // Usar unidade se disponível
                unidadeUsar = po.unidade;
                console.log('Debug - Usando unidade da API:', unidadeUsar);
              } else if (produtoInfo && produtoInfo.unidade_medida && produtoInfo.unidade_medida !== null) {
                // Se não tem nas APIs, usar a do produto
                unidadeUsar = produtoInfo.unidade_medida;
                console.log('Debug - Usando unidade do produto:', unidadeUsar);
              } else {
                console.log('Debug - Usando unidade padrão:', unidadeUsar);
              }

              return {
                id: po.id,
                id_produto: po.id_produto,
                quantidade: po.quantidade,
                valor_referencia: po.valor_referencia,
                valor_custo: po.valor_custo,
                fornecedor_id: po.fornecedor_id || undefined,
                valor_orcado: po.valor_orcado || undefined,
                valor_proposta: po.valor_proposta || undefined,
                numero_edital: po.numero_edital || '',
                custo_logistico: po.custo_logistico || 0,
                custos_adicionais: po.custos_adicionais || 0,
                percentual_imposto: po.percentual_imposto || 0,
                unidade: unidadeUsar,
                descricao_especifica: po.descricao_especifica || '',
                variante_id: po.variante_id || 1
              };
            });

            // NOVA ORDENAÇÃO: Ordenar produtos pelo número do edital
            const produtosOrdenados = produtosFormatados.sort((a, b) => {
              // Função para extrair número do edital ou usar 0 se vazio/inválido
              const getNumeroEdital = (produto: ProdutosOportunidadeInput): number => {
                if (!produto.numero_edital || produto.numero_edital.trim() === '') {
                  return 0; // Produtos sem número do edital vão para o final
                }

                // Extrair apenas os números do texto
                const match = produto.numero_edital.match(/\d+/);
                return match ? parseInt(match[0], 10) : 0;
              };

              const numeroA = getNumeroEdital(a);
              const numeroB = getNumeroEdital(b);

              // Ordenação: números menores primeiro, produtos sem número por último
              if (numeroA === 0 && numeroB === 0) {
                // Se ambos não têm número, manter ordem original ou ordenar alfabeticamente pelo nome do produto
                return 0;
              }
              if (numeroA === 0) return 1;  // a vai para o final
              if (numeroB === 0) return -1; // b vai para o final

              return numeroA - numeroB; // Ordem crescente por número
            });

            console.log('Debug - Produtos ordenados por número do edital:', produtosOrdenados);

            // Atualizar o state dos produtos também para garantir consistência
            setProdutos(produtosRes.data);

            setFormData(prev => {
              const updatedFormData = { ...prev, produtos: produtosOrdenados };
              console.log('Debug - formData atualizado com produtos ordenados:', updatedFormData.produtos);

              // CORREÇÃO: Calcular valor total considerando valor_proposta
              atualizarValorTotal(produtosOrdenados);

              return updatedFormData;
            });
          }
        }
      } catch (error) {
        console.error('Erro ao carregar produtos:', error);
        setError(getErrorMessage(error));
      } finally {
        setLoadingProdutos(false);
      }
    };

    carregarProdutos();
  }, [isEditing, initialData?.id]);

  const adicionarProduto = () => {
    if (produtos.length === 0) {
      toast.error("Não há produtos disponíveis");
      return;
    }

    const produtoPadrao = produtos[0];

    // Verificar se o produto já existe na lista
    const produtosExistentes = formData.produtos.filter(p => p.id_produto === produtoPadrao.id);

    // Calcular próximo variante_id
    const proximaVariante = produtosExistentes.length > 0
      ? Math.max(...produtosExistentes.map(p => p.variante_id || 1)) + 1
      : 1;

    const novoProduto: ProdutosOportunidadeInput = {
      id_produto: produtoPadrao.id,
      variante_id: proximaVariante,
      quantidade: 1,
      valor_referencia: produtoPadrao.preco_padrao,
      valor_custo: produtoPadrao.preco_padrao,
      descricao: produtoPadrao.descricao || '',
      unidade: produtoPadrao.unidade_medida || 'unidade', // Usar unidade_medida do produto se não for NULL
      descricao_especifica: '' // Novo campo: descrição específica da oportunidade
    };

    setFormData(prev => ({
      ...prev,
      produtos: [...prev.produtos, novoProduto]
    }));

    // Sincronizar com produtosOportunidade também
    setProdutosOportunidade(prev => [...prev, novoProduto]);

    // Atualizar o valor total da licitação
    atualizarValorTotal([...formData.produtos, novoProduto]);
  };

  const removerProduto = (index: number) => {
    const novosProdutos = formData.produtos.filter((_, i) => i !== index);

    setFormData(prev => ({
      ...prev,
      produtos: novosProdutos
    }));

    // Atualizar o valor total da licitação
    atualizarValorTotal(novosProdutos);
  };

  // Atualizar a função alterarProduto no OpportunityForm.tsx para incluir o número do edital

  const alterarProduto = (index: number, campo: keyof ProdutosOportunidadeInput, valor: any) => {
    const novosProdutos = [...formData.produtos];

    if (campo === 'id_produto') {
      // Quando mudar o produto, atualizar os valores padrão
      const idProdutoNovo = parseInt(valor);
      const produtoSelecionado = produtos.find(p => p.id === idProdutoNovo);

      if (produtoSelecionado) {
        // Verificar produtos existentes com o mesmo id
        const produtosExistentes = formData.produtos.filter(
          (p, i) => p.id_produto === idProdutoNovo && i !== index
        );

        // Calcular próximo variante_id
        const proximaVariante = produtosExistentes.length > 0
          ? Math.max(...produtosExistentes.map(p => p.variante_id || 1)) + 1
          : 1;

        // Determinar a unidade a usar: usar unidade_medida do produto se disponível, senão manter a atual
        const unidadeUsar = (produtoSelecionado.unidade_medida && produtoSelecionado.unidade_medida !== null)
          ? produtoSelecionado.unidade_medida
          : (novosProdutos[index].unidade || 'unidade');

        novosProdutos[index] = {
          ...novosProdutos[index],
          id_produto: produtoSelecionado.id,
          valor_custo: produtoSelecionado.preco_padrao,
          valor_referencia: produtoSelecionado.preco_padrao,
          descricao: produtoSelecionado.descricao || '',
          // Manter o valor do número do edital se já existir
          numero_edital: novosProdutos[index].numero_edital || '',
          // Usar a unidade do produto se disponível
          unidade: unidadeUsar,
          descricao_especifica: novosProdutos[index].descricao_especifica || ''
        };
      }
    } else if (campo === 'valor_referencia' || campo === 'valor_custo' || campo === 'valor_orcado' || campo === 'valor_proposta') {
      // Para campos de valor, aceitar vírgula como separador decimal
      let valorNumerico = valor.toString().replace(/\./g, '').replace(',', '.');
      valorNumerico = parseFloat(valorNumerico) || 0;

      novosProdutos[index] = {
        ...novosProdutos[index],
        [campo]: valorNumerico
      };
    } else {
      // Para outros campos, apenas atualizar o valor
      novosProdutos[index] = {
        ...novosProdutos[index],
        [campo]: ['quantidade'].includes(campo)
          ? parseFloat(valor.toString().replace(/\./g, '').replace(',', '.'))
          : valor
      };
    }

    setFormData(prev => ({
      ...prev,
      produtos: novosProdutos
    }));

    setProdutosOportunidade(novosProdutos);

    // Atualizar o valor total da licitação
    atualizarValorTotal(novosProdutos);
  };

  const atualizarValorTotal = (produtos: ProdutosOportunidadeInput[]) => {
    // CORREÇÃO: Usar valor_proposta quando disponível, senão valor_referencia
    const valorTotal = produtos.reduce((total, produto) => {
      const valorUsar = produto.valor_proposta !== null && produto.valor_proposta !== undefined
        ? produto.valor_proposta
        : produto.valor_referencia;
      return total + (produto.quantidade * valorUsar);
    }, 0);

    // Formatar o valor para exibição
    const valorFormatado = formatCurrency(valorTotal);

    // Atualizar o campo bidValue no formulário
    setFormData(prev => ({
      ...prev,
      bidValue: valorFormatado
    }));
  };

  const handleCampoPersonalizadoChange = (campoId: number, valor: any) => {
    console.log(`Alterando campo ${campoId}:`, valor);

    // Encontrar o tipo do campo para tratamento específico
    const campo = camposPersonalizados.find(c => c.id === campoId);

    let valorFinal = valor;

    // Tratamentos específicos por tipo
    if (campo) {
      if (campo.tipo_campo === 'checkbox') {
        // Para interface, mantemos como booleano
        valorFinal = valor === true || valor === "1";
        console.log(`Convertendo checkbox para interface: ${valorFinal} (${typeof valorFinal})`);
      }
      else if (campo.tipo_campo === 'numero' || campo.tipo_campo === 'porcentagem') {
        // Garantir que seja número ou string numérica
        if (valor === '' || valor === null || valor === undefined) {
          valorFinal = '0';
        }
      }
      else if (campo.tipo_campo === 'select') {
        // Garantir que seja string para select
        if (valor === null || valor === undefined) {
          valorFinal = '';
        }
      }
    }

    console.log(`Valor final para ${campoId}:`, valorFinal);

    setValoresCamposPersonalizados(prev => ({
      ...prev,
      [campoId]: valorFinal
    }));
  };

  const renderizarCampoPersonalizado = (campo: CustomField) => {
    const valor = valoresCamposPersonalizados[campo.id];
    console.log(`Renderizando campo ${campo.id} (${campo.tipo_campo}):`, valor);

    switch (campo.tipo_campo) {
      case 'texto':
      case 'email':
      case 'telefone':
        return (
          <input
            type={campo.tipo_campo === 'email' ? 'email' : 'text'}
            className="form-control"
            value={valor || ''}
            onChange={(e) => handleCampoPersonalizadoChange(campo.id, e.target.value)}
            placeholder={campo.placeholder}
          />
        );

      case 'numero':
        return (
          <input
            type="number"
            className="form-control"
            value={valor || ''}
            onChange={(e) => handleCampoPersonalizadoChange(campo.id, e.target.value)}
            placeholder={campo.placeholder}
            min={campo.validacoes?.min}
            max={campo.validacoes?.max}
          />
        );

      case 'textarea':
        return (
          <textarea
            className="form-control"
            value={valor || ''}
            onChange={(e) => handleCampoPersonalizadoChange(campo.id, e.target.value)}
            placeholder={campo.placeholder}
          />
        );

      case 'select':
        return (
          <Select
            className="select2"
            options={campo.opcoes?.map(opt => ({
              value: opt.id.toString(),
              label: opt.label
            }))}
            value={valor ? { value: valor, label: campo.opcoes?.find(opt => opt.id.toString() === valor)?.label } : null}
            onChange={(option) => handleCampoPersonalizadoChange(campo.id, option?.value)}
            placeholder={campo.placeholder || "Selecione"}
            classNamePrefix="react-select"
            isClearable
          />
        );

      case 'multiselect':
        return (
          <Select
            className="select2"
            isMulti
            options={campo.opcoes?.map(opt => ({
              value: opt.id.toString(),
              label: opt.label
            }))}
            value={valor ? valor.map((v: string) => ({
              value: v,
              label: campo.opcoes?.find(opt => opt.id.toString() === v)?.label
            })) : []}
            onChange={(options) => handleCampoPersonalizadoChange(campo.id, options.map(opt => opt.value))}
            placeholder={campo.placeholder || "Selecione"}
            classNamePrefix="react-select"
          />
        );

      case 'checkbox':
        return (
          <div className="d-flex align-items-center">
            <input
              type="checkbox"
              className="form-check-input me-2"
              checked={valor === "1" || valor === true}
              onChange={(e) => handleCampoPersonalizadoChange(campo.id, e.target.checked ? "1" : "0")}
              style={{ marginTop: 0 }}
            />
            <span>{campo.titulo}</span>
          </div>
        );

      case 'data':
        let dataSelecionada: Date | null = null;
        if (valor) {
          if (valor instanceof Date) {
            dataSelecionada = valor;
          } else if (typeof valor === 'string') {
            // Tenta detectar formato DD/MM/YYYY
            if (valor.match(/^\d{2}\/\d{2}\/\d{4}$/)) {
              const [dia, mes, ano] = valor.split('/').map(Number);
              dataSelecionada = new Date(ano, mes - 1, dia);
            } else if (valor.includes('T') || valor.includes('-')) {
              // Formato ISO ou YYYY-MM-DD
              dataSelecionada = new Date(valor);
            } else {
              // Tenta criar direto (fallback)
              dataSelecionada = new Date(valor);
            }
          }
        }

        // Verificar se a data é válida
        if (dataSelecionada && isNaN(dataSelecionada.getTime())) {
          dataSelecionada = null;
        }

        return (
          <DatePicker
            className="form-control"
            selected={dataSelecionada}
            onChange={(date) => handleCampoPersonalizadoChange(campo.id, date)}
            dateFormat="dd/MM/yyyy"
            placeholderText={campo.placeholder || "dd/mm/aaaa"}
            locale="pt-BR"
          />
        );

      case 'monetario':
        return (
          <input
            type="text"
            className="form-control"
            value={valor || ''}
            onChange={(e) => {
              const valorNumerico = e.target.value.replace(/\D/g, '');
              const valorFormatado = new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL'
              }).format(Number(valorNumerico) / 100);
              handleCampoPersonalizadoChange(campo.id, valorFormatado);
            }}
            placeholder={campo.placeholder || "R$ 0,00"}
          />
        );

      case 'porcentagem':
        return (
          <input
            type="number"
            className="form-control"
            value={valor || ''}
            onChange={(e) => handleCampoPersonalizadoChange(campo.id, e.target.value)}
            placeholder={campo.placeholder || "0%"}
            min="0"
            max="100"
            step="0.01"
          />
        );

      default:
        return null;
    }
  };

  const handleContactChange = async (option: SelectOption | null) => {
    try {
      if (option) {
        console.log('Contato selecionado:', option);
        handleInputChange('contact', option);

        const contactResponse = await apiService.getContatoById(Number(option.value));
        console.log('Resposta do contato:', contactResponse);

        if (contactResponse.success && Array.isArray(contactResponse.data)) {
          // Agora verificamos se é um array antes de usar find
          const contatoEncontrado = contactResponse.data.find(
            (contato: Contato) => contato.id === Number(option.value)
          );

          if (contatoEncontrado) {
            // Armazenar UF do contato
            const ufContato = contatoEncontrado.municipio_uf || '';
            console.log('UF do contato encontrada:', ufContato);

            // Adicionar UF ao formData
            setFormData(prev => ({
              ...prev,
              contatoUf: ufContato
            }));

            if (contatoEncontrado.id_municipio) {
              console.log('ID do município encontrado:', contatoEncontrado.id_municipio);

              const municipalityOption = {
                value: contatoEncontrado.id_municipio,
                label: contatoEncontrado.municipio_nome
              };

              console.log('Opção de município a ser setada:', municipalityOption);
              handleInputChange('municipality', municipalityOption);
            } else {
              handleInputChange('municipality', null);
            }
          } else {
            console.log('Contato não encontrado na resposta');
            setFormData(prev => ({ ...prev, contatoUf: '' }));
            handleInputChange('municipality', null);
          }
        } else {
          console.log('Contato não tem id_municipio ou não foi encontrado');
          setFormData(prev => ({ ...prev, contatoUf: '' }));
          handleInputChange('municipality', null);
        }
      } else {
        handleInputChange('contact', null);
        handleInputChange('municipality', null);
        setFormData(prev => ({ ...prev, contatoUf: '' }));
      }
    } catch (error) {
      console.error('Erro detalhado ao buscar município:', error);
      setError(getErrorMessage(error));
      // Em caso de erro, limpar a UF
      setFormData(prev => ({ ...prev, contatoUf: '' }));
    }
  };

  // Handler para quando um contato é criado via formulário rápido
  const handleQuickContactCreated = async (newContact: any) => {
    try {
      // Criar opção para o novo contato
      const novoContatoOption = {
        value: newContact.id,
        label: newContact.tipo_entidade === 'PF' ? newContact.nome : newContact.razao_social || newContact.nome
      };

      // 1. Adicionar à lista de forma otimista (atualização instantânea)
      setContacts(prev => [...prev, novoContatoOption]);

      // 2. Fechar o formulário inline imediatamente
      setShowQuickContactForm(false);

      // 3. Selecionar automaticamente o novo contato e aplicar toda a lógica
      await handleContactChange(novoContatoOption);

      // 4. Feedback visual sucinto
      toast.success('✓ Cliente cadastrado e selecionado', {
        autoClose: 2000,
        position: 'top-right'
      });

      // 5. Recarregar lista completa em background (sincronização)
      apiService.getContatos().then(contatosResponse => {
        if (contatosResponse.success && Array.isArray(contatosResponse.data)) {
          const contatosOptions = contatosResponse.data.map((contato: Contato) => ({
            value: contato.id,
            label: contato.tipo_entidade === 'PF' ? contato.nome : contato.razao_social || contato.nome
          }));
          setContacts(contatosOptions);
        }
      }).catch(err => {
        console.warn('Erro ao sincronizar lista de contatos:', err);
      });

    } catch (error) {
      console.error('Erro ao processar novo contato:', error);
      toast.error('Erro ao selecionar o cliente', {
        autoClose: 3000
      });
      setShowQuickContactForm(false);
    }
  };

  // Handler para quando uma origem é criada via formulário rápido
  const handleQuickOriginCreated = async (newOrigin: any) => {
    try {
      // Criar opção para a nova origem
      const novaOrigemOption = {
        value: newOrigin.id,
        label: newOrigin.nome
      };

      // 1. Adicionar à lista de forma otimista (atualização instantânea)
      setSourceOptions(prev => [...prev, novaOrigemOption]);

      // 2. Fechar o formulário inline imediatamente
      setShowQuickOriginForm(false);

      // 3. Selecionar automaticamente a nova origem
      handleInputChange('source', novaOrigemOption);

      // 4. Feedback visual sucinto
      toast.success('✓ Origem cadastrada e selecionada', {
        autoClose: 2000,
        position: 'top-right'
      });

      // 5. Recarregar lista completa em background (sincronização)
      apiService.getOrigens().then(origensResponse => {
        if (origensResponse.success && Array.isArray(origensResponse.data)) {
          const origensOptions = origensResponse.data.map((origem: any) => ({
            value: origem.id,
            label: origem.nome
          }));
          setSourceOptions(origensOptions);
        }
      }).catch(err => {
        console.warn('Erro ao sincronizar lista de origens:', err);
      });

    } catch (error) {
      console.error('Erro ao processar nova origem:', error);
      toast.error('Erro ao selecionar a origem', {
        autoClose: 3000
      });
      setShowQuickOriginForm(false);
    }
  };

  // Handler para quando um produto é criado via formulário rápido
  const handleQuickProductCreated = async (newProduct: any) => {
    try {
      // 1. Adicionar à lista de produtos disponíveis de forma otimista
      setProdutos(prev => [...prev, newProduct]);

      // 2. Fechar o formulário inline imediatamente
      setShowQuickProductForm(false);

      // 3. Criar objeto do produto para adicionar na oportunidade
      const novoProdutoOportunidade: ProdutosOportunidadeInput = {
        id_produto: newProduct.id,
        variante_id: 1, // Primeira variante deste produto
        quantidade: 1,
        valor_referencia: newProduct.preco_padrao || 0,
        valor_custo: newProduct.preco_padrao || 0,
        descricao: newProduct.descricao || '',
        unidade: newProduct.unidade_medida || 'unidade',
        descricao_especifica: ''
      };

      // 4. Adicionar à lista de produtos da oportunidade
      const novosProdutos = [...formData.produtos, novoProdutoOportunidade];

      setFormData(prev => ({
        ...prev,
        produtos: novosProdutos
      }));

      // 5. Sincronizar com produtosOportunidade
      setProdutosOportunidade(prev => [...prev, novoProdutoOportunidade]);

      // 6. Atualizar o valor total da licitação
      atualizarValorTotal(novosProdutos);

      // 7. Feedback visual sucinto
      toast.success('✓ Produto cadastrado e adicionado à oportunidade', {
        autoClose: 2000,
        position: 'top-right'
      });

      // 8. Recarregar lista completa de produtos em background (sincronização)
      apiService.getProdutos().then(produtosResponse => {
        if (produtosResponse.success && Array.isArray(produtosResponse.data)) {
          setProdutos(produtosResponse.data);
        }
      }).catch(err => {
        console.warn('Erro ao sincronizar lista de produtos:', err);
      });

    } catch (error) {
      console.error('Erro ao processar novo produto:', error);
      toast.error('Erro ao adicionar o produto', {
        autoClose: 3000
      });
      setShowQuickProductForm(false);
    }
  };

  // Handler para quando um status é criado via formulário rápido
  const handleQuickStatusCreated = async (newStatus: any) => {
    try {
      // Criar opção para o novo status
      const novoStatusOption = {
        value: newStatus.id,
        label: newStatus.nome,
        e_final: newStatus.e_final === "1" || newStatus.e_final === 1 || newStatus.e_final === true
      };

      // 1. Adicionar à lista de forma otimista (atualização instantânea)
      setStatusList(prev => [...prev, novoStatusOption]);

      // 2. Fechar o formulário inline imediatamente
      setShowQuickStatusForm(false);

      // 3. Selecionar automaticamente o novo status
      handleInputChange('status', novoStatusOption);

      // 4. Feedback visual sucinto
      toast.success('✓ Status cadastrado e selecionado', {
        autoClose: 2000,
        position: 'top-right'
      });

      // 5. Recarregar lista completa em background (sincronização)
      apiService.getStatusOportunidades().then(statusResponse => {
        if (statusResponse.success && Array.isArray(statusResponse.data)) {
          const statusOptions = statusResponse.data.map((status: any) => ({
            value: status.id,
            label: status.nome,
            e_final: status.e_final === "1" || status.e_final === 1 || status.e_final === true
          }));
          setStatusList(statusOptions);
        }
      }).catch(err => {
        console.warn('Erro ao sincronizar lista de status:', err);
      });

    } catch (error) {
      console.error('Erro ao processar novo status:', error);
      toast.error('Erro ao selecionar o status', {
        autoClose: 3000
      });
      setShowQuickStatusForm(false);
    }
  };

  const handleQuickUnitCreated = async (newUnit: any) => {
    try {
      // 1. Fechar o formulário inline imediatamente
      setShowQuickUnitForm(false);

      // 2. Recarregar a lista de unidades
      await refetchUnidadesMedida();

      // 3. Feedback visual
      toast.success('✓ Unidade de medida cadastrada', {
        autoClose: 2000,
        position: 'top-right'
      });

    } catch (error) {
      console.error('Erro ao processar nova unidade:', error);
      toast.error('Erro ao atualizar lista de unidades', {
        autoClose: 3000
      });
      setShowQuickUnitForm(false);
    }
  };

  // Handler para mudanças nos inputs com lógica para mostrar o modal de contrato
  const handleInputChange = (name: keyof IFormData, value: any) => {
    // Se for alteração de status e for para "Ganha"
    if (name === 'status' && value?.label === "Ganha") {
      setStatusAnterior(formData.status);

      // Verifica se a etapa atual permite gerar contrato
      if (podeGerarContrato()) {
        setShowModalContrato(true);
        setFormData(prev => ({
          ...prev,
          [name]: value
        }));
        return;
      }
    }

    // Caso seja alteração de etapa e já esteja com status "Ganha"
    if (name === 'funnelStage' && formData.status?.label === "Ganha") {
      // Criamos um objeto temporário para verificar se a nova etapa permite contrato
      const tempFormData = { ...formData, [name]: value };
      const etapaId = Number(value?.value);
      const etapa = selectedFunnel?.etapas?.find(e => e.id === etapaId);

      if (etapa?.gera_contrato === true) {
        setShowModalContrato(true);
      }
    }

    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Adicione as funções para lidar com o modal de contrato
  const handleConfirmModal = (datas?: { data_inicio: string; data_fim: string; prazo_vencimento: number }) => {
    // Armazenar as datas do contrato se fornecidas
    if (datas) {
      setDatasContrato(datas);
    }
    setCriarContrato(true);
    setShowModalContrato(false);
  };

  const handleCancelModal = () => {
    // Restaura o status anterior
    if (statusAnterior) {
      setFormData(prev => ({
        ...prev,
        status: statusAnterior
      }));
    }
    setShowModalContrato(false);
    setCriarContrato(false);
    setDatasContrato(null);
  };

  const handleCurrencyInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const numericValue = value.replace(/\D/g, '');
    const formattedValue = new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(Number(numericValue) / 100);

    handleInputChange('bidValue', formattedValue);
  };

  // Função auxiliar para obter ID do usuário logado
  const getUserId = (): number => {
    // Implementar lógica para recuperar ID do usuário 
    // Exemplo: usar token, localStorage, contexto de autenticação
    const userInfo = localStorage.getItem('user');
    return userInfo
      ? JSON.parse(userInfo).id
      : 0; // Valor padrão ou tratamento de erro
  };

  const getUserInfo = (): { id: number, name: string } => {
    const userInfo = localStorage.getItem('user');
    if (userInfo) {
      const parsedUserInfo = JSON.parse(userInfo);
      return {
        id: parsedUserInfo.id,
        name: parsedUserInfo.nome || parsedUserInfo.name
      };
    }
    return { id: 0, name: 'Usuário' }; // Valor padrão de segurança
  };

  const getBgColor = (stage: string): string => {
    const stageColorMap: Record<string, string> = {
      'Prospecção': 'bg-purple',
      'Avaliação': 'bg-warning',
      'Documentação': 'bg-success',
      'Análise': 'bg-danger'
    };
    return stageColorMap[stage] || 'bg-primary';
  };

  const resetForm = () => {
    // Obter as informações do usuário atual para o dono padrão
    const userInfo = getUserInfo();
    const ownerOption: SelectOption = {
      value: userInfo.id,
      label: userInfo.name
    };

    // Definir o funil selecionado no kanban
    const funnelOption: SelectOption | null = selectedFunnel ? {
      value: selectedFunnel.id,
      label: selectedFunnel.nome
    } : null;

    // Encontrar a primeira etapa do funil
    let initialStage: SelectOption | null = null;

    // Verificação segura para TypeScript
    if (selectedFunnel && selectedFunnel.etapas && selectedFunnel.etapas.length > 0) {
      // Criamos uma cópia do array de etapas para evitar modificar o original
      // Usamos uma afirmação não-nula (!) para garantir ao TypeScript que etapas existe
      const sortedStages = [...selectedFunnel.etapas].sort((a, b) => {
        const ordemA = typeof a.ordem === 'number' ? a.ordem : 0;
        const ordemB = typeof b.ordem === 'number' ? b.ordem : 0;
        return ordemA - ordemB;
      });

      const firstStage = sortedStages[0];
      if (firstStage && firstStage.id !== undefined) {
        initialStage = {
          value: firstStage.id,
          label: firstStage.nome || 'Sem nome'
        };
      }
    }

    // Encontrar o status "Aberta" na lista dinâmica para ser o padrão
    const defaultStatus = statusList.find(s => s.label === "Aberta") || { value: "Aberta", label: "Aberta" };

    // Resetar todos os campos para valores padrão
    setFormData({
      numero_contrato: '',
      title: '',
      source: null,
      funnel: funnelOption,
      funnelStage: initialStage,
      owner: ownerOption,
      contact: null,
      municipality: null,
      bidValue: '',
      status: defaultStatus,
      produtos: [],
      url_site_externo: '',
      uasb: '',
      tempo_entrega: { value: "Sem informação", label: "Sem informação" },
      contatoUf: '' // Resetar UF do contato
    });

    // Resetar outros estados
    setBidDateTime(new Date());
    setProdutosOportunidade([]);
    setValoresCamposPersonalizados({});
    setCriarContrato(false);
    setStatusAnterior(null);
    setDatasContrato(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      // Ativar o estado de carregamento
      setLoading(true);
      setError(null);

      console.log('Debug - Produtos no momento do envio:', {
        produtos_no_formData: formData.produtos,
        produtos_no_state: produtosOportunidade,
        quantidade_produtos: formData.produtos.length
      });

      console.log('Debug - Produtos antes do envio:', formData.produtos);

      // Validações existentes
      if (!formData.funnel || !formData.funnelStage || !formData.source ||
        !formData.owner || !formData.contact || !formData.municipality || !formData.status) {
        // Definir a mensagem de erro
        setError('Todos os campos obrigatórios precisam ser preenchidos');

        // Desativar o carregamento
        setLoading(false);

        // Usar setTimeout para garantir que o scroll ocorra após a renderização
        setTimeout(() => {
          // Tentar múltiplos métodos de scroll para maior compatibilidade
          if (formTopRef.current) {
            formTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            // Fallback para navegadores mais antigos
            document.body.scrollTop = 0; // Para Safari
            document.documentElement.scrollTop = 0; // Para Chrome, Firefox, IE e Opera
          }
        }, 100);

        return;
      }

      // Nova validação: verificar se há pelo menos um produto
      if (formData.produtos.length === 0) {
        setError('Adicione pelo menos um produto à oportunidade');
        setLoading(false);

        setTimeout(() => {
          if (formTopRef.current) {
            formTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          } else {
            window.scrollTo({ top: 0, behavior: 'smooth' });
            document.body.scrollTop = 0;
            document.documentElement.scrollTop = 0;
          }
        }, 100);

        return;
      }

      // Garantir que formData.funnel e formData.funnelStage não são null
      if (formData.funnel && formData.funnelStage) {
        // Preparar os campos personalizados - obter TODOS os campos da etapa atual
        const camposEtapa = await apiService.getCustomFields(
          Number(formData.funnel.value),
          Number(formData.funnelStage.value)
        );

        console.log('Todos os campos disponíveis na etapa:', camposEtapa.data);

        // Preparar valores para TODOS os campos da etapa atual, sem filtro de exibir_criacao
        const camposParaSalvar = camposEtapa.data.map(campo => {
          // Verificar se já existe valor preenchido pelo usuário
          const valorAtual = valoresCamposPersonalizados[campo.id];
          let valorFinal;

          if (valorAtual !== undefined && valorAtual !== null && valorAtual !== '') {
            // Se já tem valor, usar o valor existente (formatado conforme tipo)
            if (campo.tipo_campo === 'checkbox') {
              valorFinal = valorAtual === true || valorAtual === "1" ? "1" : "0";
            } else {
              valorFinal = valorAtual;
            }
          } else {
            // Se não tem valor, usar valor padrão conforme tipo
            switch (campo.tipo_campo) {
              case 'checkbox':
                valorFinal = "0";
                break;
              case 'numero':
              case 'monetario':
              case 'porcentagem':
                valorFinal = "0";
                break;
              case 'select':
              case 'multiselect':
              case 'radio':
                // Se tiver opções, tenta usar a primeira opção
                try {
                  if (campo.opcoes && typeof campo.opcoes === 'string') {
                    const opcoesParsed = JSON.parse(campo.opcoes);
                    if (Array.isArray(opcoesParsed) && opcoesParsed.length > 0) {
                      valorFinal = opcoesParsed[0].id.toString();
                    } else {
                      valorFinal = "";
                    }
                  } else if (campo.opcoes && Array.isArray(campo.opcoes) && campo.opcoes.length > 0) {
                    valorFinal = campo.opcoes[0].id.toString();
                  } else {
                    valorFinal = "";
                  }
                } catch (err) {
                  console.error('Erro ao processar opções do campo:', err);
                  valorFinal = "";
                }
                break;
              case 'data':
              case 'hora':
                valorFinal = null;
                break;
              default:
                valorFinal = campo.valor_padrao || "";
            }
          }

          console.log(`Campo ${campo.id} (${campo.tipo_campo}): ${valorFinal}`);

          return {
            id_campo_personalizado: Number(campo.id),
            valor: valorFinal
          };
        });

        console.log('Campos preparados para salvar (todos):', camposParaSalvar);

        // Continuar com o código existente...
        let tituloFinal = formData.title.trim();
        if (!tituloFinal && formData.contact) {
          tituloFinal = formData.contact.label;

          // Adicionar UF se disponível
          if (formData.contatoUf) {
            tituloFinal += ` - ${formData.contatoUf}`;
          }

          // Adicionar número do contrato
          tituloFinal += ` - ${formData.numero_contrato}`;
        }

        // Converter o valor da licitação
        const valorLicitacao = parseFloat(formData.bidValue.replace(/[R$\s.]/g, '').replace(',', '.'));

        const isStatusId = typeof formData.status?.value === 'number' || !isNaN(Number(formData.status?.value));

        const apiData: CreateOpportunityData = {
          id_conta: 2,
          numero_contrato: formData.numero_contrato,
          titulo: tituloFinal,
          id_funil: Number(formData.funnel.value),
          id_etapa: Number(formData.funnelStage.value),
          id_origem: Number(formData.source.value),
          id_dono: Number(formData.owner.value),
          id_contato: Number(formData.contact.value),
          id_municipio: Number(formData.municipality?.value),
          valor_licitacao: valorLicitacao,
          status: isStatusId ? undefined : formData.status?.value as string,
          id_status: isStatusId ? Number(formData.status?.value) : undefined,
          data_licitacao: bidDateTime ? bidDateTime.toISOString() : '',
          probabilidade: 50,
          campos_personalizados: {}, // Vamos gerenciar separadamente
          criado_por: getUserId(),
          criarContrato: criarContrato,
          produtos: formData.produtos,
          url_site_externo: formData.url_site_externo,
          uasb: formData.uasb,
          tempo_entrega: formData.tempo_entrega?.value as TempoEntrega,
          // Datas do contrato (se houver)
          contrato_data_inicio: datasContrato?.data_inicio,
          contrato_data_fim: datasContrato?.data_fim,
          contrato_prazo_vencimento: datasContrato?.prazo_vencimento
        };

        console.log('DEBUG [OpportunityForm] - Título gerado:', tituloFinal);
        console.log('DEBUG [OpportunityForm] - API data completa (payload):', apiData);
        console.log('DEBUG [OpportunityForm] - Detalhe das Unidades nos Produtos:', apiData.produtos?.map(p => ({
          produto: p.id_produto,
          unidade_enviada: p.unidade,
          edital: p.numero_edital
        })));

        if (!isEditing) {
          // CRIAÇÃO DE NOVA OPORTUNIDADE
          console.log('Criando oportunidade com dados:', apiData);
          console.log('Debug - Produtos sendo enviados na criação:', apiData.produtos);
          const response = await apiService.createOpportunity(apiData);

          if (response.success) {
            console.log('Oportunidade criada com sucesso, ID:', response.data.id);

            // IMPORTANTE: Salvar campos personalizados explicitamente
            if (camposParaSalvar.length > 0) {
              console.log('Salvando campos personalizados para oportunidade ID:', response.data.id);
              try {
                const resCampos = await apiService.saveFieldValues(response.data.id, camposParaSalvar);
                console.log('Resultado do salvamento de campos:', resCampos);
              } catch (camposError) {
                console.error('Erro ao salvar campos personalizados:', camposError);
                // Opcional: mostrar alerta sobre falha parcial
              }
            } else {
              console.log('Não há campos personalizados para salvar');
            }

            // Em OpportunityForm.tsx, modifique a criação do formattedOpportunity
            const formattedOpportunity = {
              id: response.data.id.toString(),
              title: tituloFinal,
              value: new Intl.NumberFormat('pt-BR', {
                style: 'currency',
                currency: 'BRL'
              }).format(response.data.valor_licitacao),
              status: response.data.status,
              bidDate: new Date(response.data.data_licitacao),
              municipio: {
                id: Number(formData.municipality?.value),
                nome: formData.municipality?.label || '',
                uf: response.data.municipio_uf || ''
              },
              funnel: formData.funnel.label,
              stage: formData.funnelStage.label,
              funnelStage: formData.funnelStage.label,
              numero_contrato: formData.numero_contrato,
              uasb: formData.uasb,
              tempo_entrega: formData.tempo_entrega,
              owner: {
                name: formData.owner.label,
                avatar: undefined
              },
              contact: formData.contact.label,
              id_contato: Number(formData.contact.value),
              bgColor: getBgColor(formData.funnelStage.label),
              progress: 0,
              source: formData.source.label
            };

            onOpportunityCreated?.(formattedOpportunity);

            resetForm();

            // Verificar se deve fechar o modal
            if (closeOnSuccess !== false) { // Se não for explicitamente false, fecha o modal
              onClose?.();
            }

            setOpenModal(true);
            setLoading(false); // Desativa o carregamento após o sucesso
          }
        } else {
          console.log("DEBUG API - Enviando para edição:", {
            apiData,
            endpoint: `Oportunidade/update/${initialData?.id}`,
            método: "PUT",
            produtos_enviados: apiData.produtos?.length,
            produtos_detalhados: apiData.produtos
          });

          // Se preferir, você pode usar o console.table para ver os produtos de forma mais organizada
          console.table(apiData.produtos);


          // EDIÇÃO DE OPORTUNIDADE EXISTENTE
          await onSubmit(apiData);

          // Se for edição, certifique-se de que a implementação do onSubmit também salve os campos
          if (initialData?.id) {

            try {
              // CORREÇÃO: Converter ID para número
              await apiService.saveFieldValues(Number(initialData.id), camposParaSalvar);
            } catch (camposError) {
              console.error('Erro ao salvar campos personalizados (edição):', camposError);
            }
          }

          resetForm();
        }
      } else {
        setError('Funil e etapa são obrigatórios');
        setLoading(false);
      }
    } catch (error) {
      const mensagemErro = error instanceof Error
        ? error.message
        : 'Erro ao processar oportunidade';

      setError(mensagemErro);
      console.error('Erro:', error);

      setLoading(false);

      setTimeout(() => {
        if (formTopRef.current) {
          formTopRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
        } else {
          window.scrollTo({ top: 0, behavior: 'smooth' });
          document.body.scrollTop = 0;
          document.documentElement.scrollTop = 0;
        }
      }, 100);
    }
  };

  // Modificar o texto do botão para refletir o modo
  const buttonText = isEditing ? 'Salvar Alterações' : 'Criar Oportunidade';
  const buttonLoadingText = isEditing ? 'Salvando...' : 'Criando...';

  // Não use return <div className="p-4">Carregando...</div> quando loading=true,
  // pois isso impede a exibição do overlay de carregamento personalizado

  return (
    <>
      <form onSubmit={handleSubmit}>
        <div ref={formTopRef} style={{ scrollMarginTop: '80px' }}></div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show" role="alert">
            <div className="d-flex">
              <i className="ti ti-alert-circle me-2 fs-5 mt-1"></i>
              <div>
                <strong>Atenção:</strong> {error}
                <p className="mb-0 mt-1 text-sm">Por favor, verifique todos os campos.</p>
              </div>
            </div>
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label="Fechar"
            ></button>
          </div>
        )}

        <div className="row">
          {/* N° Contrato */}
          <div className="col-md-6">
            <div className="mb-3">
              <label className="col-form-label">
                N° Contrato <span className="text-danger">*</span>
              </label>
              <input
                type="text"
                name="numero_contrato"
                className="form-control"
                value={formData.numero_contrato}
                onChange={(e) => handleInputChange('numero_contrato', e.target.value)}
              />
            </div>
          </div>

          {/* Título */}
          <div className="col-md-6">
            <div className="mb-3">
              <label className="col-form-label">
                Título
              </label>
              <input
                type="text"
                name="title"
                className="form-control"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
              />
            </div>
          </div>

          {/* Origem */}
          <div className="col-md-6">
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <label className="col-form-label mb-0">
                  Origem <span className="text-danger">*</span>
                </label>
              </div>
              <Select
                name="source"
                className="select2"
                options={sourceOptions}
                value={formData.source}
                onChange={(option) => handleInputChange('source', option)}
                placeholder="Selecione"
                classNamePrefix="react-select"
              />

              {/* Botão de Cadastro Rápido */}
              {!showQuickOriginForm && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary mt-2 w-100"
                  onClick={() => setShowQuickOriginForm(true)}
                >
                  <i className="ti ti-plus me-1"></i>
                  Cadastrar nova origem
                </button>
              )}
            </div>
          </div>

          {/* Formulário Inline de Cadastro Rápido de Origem */}
          {showQuickOriginForm && (
            <div className="col-12">
              <div className="card border-primary mb-3 quick-origin-card">
                <div className="card-body">
                  <QuickOriginForm
                    onSuccess={handleQuickOriginCreated}
                    onCancel={() => setShowQuickOriginForm(false)}
                  />
                </div>
              </div>
            </div>
          )}

          <div className="col-md-6">
            <div className="mb-3">
              <label className="col-form-label">
                Link da Disputa
              </label>
              <div className="input-group">
                <span className="input-group-text">
                  <i className="ti-link"></i>
                </span>
                <input
                  type="text"
                  name="url_site_externo"
                  className="form-control"
                  value={formData.url_site_externo}
                  onChange={(e) => handleInputChange('url_site_externo', e.target.value)}
                  placeholder="https://exemplo.com.br"
                />
              </div>
              <small className="form-text text-muted">
                Digite a URL completa incluindo o protocolo (https:// ou http://)
              </small>
              {formData.url_site_externo && !formData.url_site_externo.match(/^https?:\/\//) && (
                <div className="text-danger mt-1">
                  <small>Lembre-se de incluir "https://" ou "http://" no início da URL</small>
                </div>
              )}
            </div>
          </div>

          {/* Etapa do Funil */}
          <div className="col-md-6">
            <div className="mb-3">
              <label className="col-form-label">
                Etapa do Funil <span className="text-danger">*</span>
              </label>
              <Select
                name="funnelStage"
                className="select2"
                options={selectedFunnel?.etapas?.map((e: any) => ({
                  value: e.id,
                  label: e.nome
                })) || []}
                value={formData.funnelStage}
                onChange={(option) => handleInputChange('funnelStage', option)}
                placeholder={selectedFunnel ? "Selecione a etapa" : "Funil não selecionado"}
                isDisabled={!selectedFunnel}
                classNamePrefix="react-select"
              />
              {/* Adicionar uma mensagem informativa sobre o funil selecionado */}
              {selectedFunnel && (
                <small className="form-text text-muted">
                  Funil: {selectedFunnel.nome || 'Não selecionado'}
                </small>
              )}
            </div>
          </div>

          {/* Dono */}
          <div className="col-md-6">
            <div className="mb-3">
              <label className="col-form-label">
                Dono <span className="text-danger">*</span>
              </label>
              <Select
                name="owner"
                className="select2"
                options={formData.owner ? [formData.owner] : []} // Verifica se owner não é nulo
                value={formData.owner}
                isDisabled={true}
                placeholder="Responsável atual"
                classNamePrefix="react-select"
              />
            </div>
          </div>

          {/* Contato */}
          <div className="col-md-6">
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <label className="col-form-label mb-0">
                  Cliente <span className="text-danger">*</span>
                </label>
              </div>
              <Select
                name="contact"
                className="select2"
                options={contacts}
                value={formData.contact}
                onChange={handleContactChange} // Usar o novo handler
                placeholder="Selecione o contato"
                classNamePrefix="react-select"
              />

              {/* Botão de Cadastro Rápido */}
              {!showQuickContactForm && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary mt-2 w-100"
                  onClick={() => setShowQuickContactForm(true)}
                >
                  <i className="ti ti-plus me-1"></i>
                  Cadastrar novo cliente
                </button>
              )}
            </div>
          </div>

          {/* Formulário Inline de Cadastro Rápido */}
          {showQuickContactForm && (
            <div className="col-12">
              <div className="card border-primary mb-3 quick-contact-card">
                <div className="card-body">
                  <QuickContactForm
                    onSuccess={handleQuickContactCreated}
                    onCancel={() => setShowQuickContactForm(false)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Municipios */}
          <div className="col-md-6">
            <div className="mb-3">
              <label className="col-form-label">
                Município <span className="text-danger">*</span>
              </label>
              <Select
                name="municipality"
                className="select2"
                options={[]}
                value={formData.municipality}
                isDisabled={true}
                placeholder={
                  formData.contact
                    ? "Carregando município..."
                    : "Selecione um contato primeiro"
                }
                classNamePrefix="react-select"
                noOptionsMessage={() => "Município será definido pelo contato"}
              />
            </div>
          </div>

          {/* Valor da Licitação */}
          <div className="col-md-6">
            <div className="mb-3">
              <label className="col-form-label">
                Valor da Licitação <span className="text-danger">*</span>
              </label>
              <input
                className="form-control"
                type="text"
                placeholder="R$ 0,00"
                value={formData.bidValue}
                onChange={handleCurrencyInput}
              />
            </div>
          </div>

          {/* UASB - Novo Campo */}
          <div className="col-md-6">
            <div className="mb-3">
              <label className="col-form-label">
                UASG
              </label>
              <input
                type="text"
                name="uasb"
                className="form-control"
                value={formData.uasb}
                onChange={(e) => handleInputChange('uasb', e.target.value)}
              />
            </div>
          </div>

          {/* Tempo de Entrega - Novo Campo */}
          <div className="col-md-6">
            <div className="mb-3">
              <label className="col-form-label">
                Tempo de Entrega <span className="text-danger">*</span>
              </label>
              <Select
                name="tempo_entrega"
                className="select2"
                options={tempoEntregaOptions}
                value={formData.tempo_entrega}
                onChange={(option) => handleInputChange('tempo_entrega', option)}
                placeholder="Selecione o tempo de entrega"
                classNamePrefix="react-select"
              />
            </div>
          </div>

          {/* Status */}
          <div className="col-md-6">
            <div className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-1">
                <label className="col-form-label mb-0">
                  Status <span className="text-danger">*</span>
                </label>
              </div>
              <Select
                name="status"
                className="select2"
                options={statusList}
                value={formData.status}
                onChange={(option) => handleInputChange('status', option)}
                placeholder="Selecione o status"
                classNamePrefix="react-select"
              />

              {/* Botão de Cadastro Rápido */}
              {!showQuickStatusForm && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-primary mt-2 w-100"
                  onClick={() => setShowQuickStatusForm(true)}
                >
                  <i className="ti ti-plus me-1"></i>
                  Cadastrar novo status
                </button>
              )}
            </div>
          </div>

          {/* Formulário Inline de Cadastro Rápido de Status */}
          {showQuickStatusForm && (
            <div className="col-12">
              <div className="card border-primary mb-3 quick-status-card">
                <div className="card-body">
                  <QuickStatusForm
                    onSuccess={handleQuickStatusCreated}
                    onCancel={() => setShowQuickStatusForm(false)}
                  />
                </div>
              </div>
            </div>
          )}

          {/* Data e Hora da Licitação */}
          <div className="col-md-6">
            <div className="mb-3">
              <label className="col-form-label">
                Data e Hora da Disputa <span className="text-danger">*</span>
              </label>
              <div className="icon-form">
                <DatePicker
                  className="form-control datetimepicker"
                  selected={bidDateTime}
                  onChange={(date) => setBidDateTime(date)}
                  showTimeSelect
                  timeFormat="HH:mm"
                  timeIntervals={15}
                  dateFormat="dd/MM/yyyy HH:mm"
                  placeholderText="dd/mm/aaaa hh:mm"
                  locale="pt-BR"
                  timeCaption="Hora"
                />
              </div>
            </div>
          </div>

          <div className="col-12 mt-4">
            <div className="card border">
              <div className="card-header bg-light d-flex justify-content-between align-items-center">
                <h6 className="mb-0">Produtos da Oportunidade</h6>
                <div className="d-flex gap-2">
                  {!showQuickProductForm && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary"
                      onClick={() => setShowQuickProductForm(true)}
                      disabled={loadingProdutos}
                    >
                      <i className="ti ti-package"></i> Cadastrar Novo Produto
                    </button>
                  )}
                  {!showQuickUnitForm && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setShowQuickUnitForm(true)}
                      disabled={loadingProdutos}
                    >
                      <i className="ti ti-settings-cog"></i> Cadastrar Nova Unidade
                    </button>
                  )}
                  <button
                    type="button"
                    className="btn btn-sm btn-primary"
                    onClick={adicionarProduto}
                    disabled={loadingProdutos}
                  >
                    <i className="ti ti-plus"></i> Adicionar Item
                  </button>
                </div>
              </div>

              {/* Formulário Inline de Cadastro Rápido de Produto */}
              {showQuickProductForm && (
                <div className="card-body border-bottom">
                  <div className="card border-primary quick-product-card">
                    <div className="card-body">
                      <QuickProductForm
                        onSuccess={handleQuickProductCreated}
                        onCancel={() => setShowQuickProductForm(false)}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Formulário Inline de Cadastro Rápido de Unidade */}
              {showQuickUnitForm && (
                <div className="card-body border-bottom">
                  <div className="card border-primary quick-unit-card">
                    <div className="card-body">
                      <QuickUnitForm
                        onSuccess={handleQuickUnitCreated}
                        onCancel={() => setShowQuickUnitForm(false)}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="card-body">
                {loadingProdutos ? (
                  <div className="text-center py-3">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Carregando...</span>
                    </div>
                    <p className="mt-2">Carregando produtos...</p>
                  </div>
                ) : formData.produtos.length === 0 ? (
                  <div className="alert alert-info mb-0">
                    <i className="ti ti-info-circle me-2"></i>
                    Nenhum produto adicionado. Clique em "Adicionar Produto" para incluir produtos nesta oportunidade.
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-bordered mb-0">
                        <thead className="table-light">
                          <tr>
                            <th style={{ width: '8%' }}>Nº Edital</th>
                            <th style={{ width: '18%' }}>Produto</th>
                            <th style={{ width: '12%' }}>Unidade</th>
                            <th style={{ width: '15%' }}>Descrição Específica</th>
                            <th style={{ width: '8%' }}>Quantidade</th>
                            <th style={{ width: '12%' }}>Valor Ref.</th>
                            <th style={{ width: '12%' }}>Subtotal</th>
                            <th style={{ width: '5%' }}>Ações</th>
                          </tr>
                        </thead>
                        <tbody>
                          {formData.produtos.map((produto, index) => {
                            // Encontra o objeto produto completo para exibir nome
                            const produtoInfo = produtos.find(p => p.id === produto.id_produto);
                            const subtotal = produto.quantidade * produto.valor_referencia;
                            const descricao = produtos.find(p => p.id === produto.id_produto)?.descricao || '';

                            return (
                              <tr key={index}>
                                <td>
                                  <input
                                    type="text"
                                    className="form-control"
                                    value={produto.numero_edital || ''}
                                    onChange={(e) => alterarProduto(index, 'numero_edital', e.target.value)}
                                    placeholder="Nº no edital"
                                  />
                                </td>
                                <td>
                                  <Select
                                    className="select2"
                                    options={produtos.map(p => ({
                                      value: p.id,
                                      label: p.nome
                                    }))}
                                    value={{
                                      value: produto.id_produto,
                                      label: produtos.find(p => p.id === produto.id_produto)?.nome || ''
                                    }}
                                    onChange={(option) => alterarProduto(index, 'id_produto', option?.value)}
                                    placeholder="Pesquise um produto..."
                                    classNamePrefix="react-select"
                                    isSearchable={true}
                                    // Aumentar o número de opções visíveis no menu
                                    maxMenuHeight={300}
                                    // Mostrar o menu acima do input quando estiver próximo ao fim da página
                                    menuPlacement="auto"
                                    // Renderizar o menu em um portal fora da hierarquia do DOM
                                    menuPortalTarget={document.body}
                                    // Garantir que o menu tenha prioridade no z-index
                                    menuPosition="fixed"
                                    // Filtragem mais inteligente
                                    filterOption={(option, inputValue) => {
                                      const label = option.label.toLowerCase();
                                      const search = inputValue.toLowerCase();
                                      // Verificar se o texto de busca está contido no nome do produto
                                      return label.includes(search);
                                    }}
                                    // Formatação personalizada para as opções
                                    formatOptionLabel={(option, { context }) => {
                                      // Customizamos apenas a visualização, não o valor
                                      const label = option.label;
                                      return (
                                        <div
                                          title={label} // Exibe o texto completo em tooltip ao passar o mouse
                                          style={{
                                            whiteSpace: 'nowrap',          // Impede quebra de linha
                                            overflow: 'hidden',            // Esconde o excesso
                                            textOverflow: 'ellipsis',      // Adiciona '...' ao final
                                            maxWidth: context === 'menu' ? '350px' : '100%'  // Limita largura na lista
                                          }}
                                        >
                                          {label}
                                        </div>
                                      );
                                    }}
                                    // Estilo customizado para melhor visualização
                                    styles={{
                                      menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 9999 // Valor alto de z-index para ficar por cima de tudo
                                      }),
                                      control: (provided) => ({
                                        ...provided,
                                        minHeight: '38px'
                                      }),
                                      menu: (provided) => ({
                                        ...provided,
                                        width: 'auto',        // Largura automática
                                        minWidth: '100%',     // No mínimo a largura do controle
                                        maxWidth: '400px'     // Largura máxima para não ficar excessivo
                                      }),
                                      container: (provided) => ({
                                        ...provided,
                                        width: '100%'        // Garantir que o container ocupe todo o espaço disponível
                                      }),
                                      menuList: (provided) => ({
                                        ...provided,
                                        paddingTop: '8px',
                                        paddingBottom: '8px'
                                      }),
                                      option: (provided, state) => ({
                                        ...provided,
                                        padding: '8px 12px',
                                        cursor: 'pointer',
                                        height: '40px',          // Altura fixa para todas as opções
                                        lineHeight: '24px',      // Alinhamento vertical do texto
                                        backgroundColor: state.isSelected
                                          ? '#0D6EFD'
                                          : state.isFocused
                                            ? '#e9ecef'
                                            : 'white',
                                        color: state.isSelected ? 'white' : 'black'
                                      }),
                                      // Também limita o texto no valor selecionado
                                      singleValue: (provided) => ({
                                        ...provided,
                                        maxWidth: '100%',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                      })
                                    }}
                                    // Mensagens em português
                                    noOptionsMessage={() => "Nenhum produto encontrado"}
                                    loadingMessage={() => "Carregando..."}
                                  />
                                </td>
                                <td>
                                  <Select
                                    className="select2"
                                    options={unidadesMedida.map(u => ({ value: u.codigo, label: `${u.nome} (${u.codigo})` }))}
                                    value={unidadesMedida.map(u => ({ value: u.codigo, label: `${u.nome} (${u.codigo})` })).find(opt => opt.value === produto.unidade) || { value: 'unidade', label: 'Unidade (unidade)' }}
                                    onChange={(option) => alterarProduto(index, 'unidade', option?.value || 'unidade')}
                                    placeholder="Selecione unidade..."
                                    classNamePrefix="react-select"
                                    isSearchable={true}
                                    menuPlacement="auto"
                                    menuPortalTarget={document.body}
                                    menuPosition="fixed"
                                    styles={{
                                      menuPortal: (base) => ({
                                        ...base,
                                        zIndex: 9999
                                      }),
                                      control: (provided) => ({
                                        ...provided,
                                        minHeight: '38px'
                                      }),
                                      menu: (provided) => ({
                                        ...provided,
                                        width: 'auto',
                                        minWidth: '150px',
                                        maxWidth: '200px'
                                      })
                                    }}
                                  />
                                </td>
                                <td>
                                  <textarea
                                    className="form-control"
                                    value={produto.descricao_especifica || ''}
                                    onChange={(e) => alterarProduto(index, 'descricao_especifica', e.target.value)}
                                    placeholder="Ex: Tipo A Premium, torra média, embalagem à vácuo..."
                                    rows={2}
                                    style={{ resize: 'vertical', minHeight: '38px' }}
                                  />
                                </td>
                                <td>
                                  <input
                                    type="text"
                                    inputMode="decimal"
                                    className="form-control"
                                    value={produto.quantidade}
                                    onChange={(e) => alterarProduto(index, 'quantidade', e.target.value)}
                                    placeholder="0"
                                    required
                                  />
                                </td>
                                <td>
                                  <div className="input-group">
                                    <span className="input-group-text">R$</span>
                                    <input
                                      type="text"
                                      inputMode="decimal"
                                      className="form-control"
                                      placeholder="0,00"
                                      value={
                                        // Usa o texto em edição se existir, senão exibe o valor numérico formatado
                                        valoresRefTexto[index] !== undefined
                                          ? valoresRefTexto[index]
                                          : formatNumberForInput(produto.valor_referencia)
                                      }
                                      onChange={(e) => {
                                        // Só permite dígitos, ponto e vírgula
                                        const raw = e.target.value;
                                        setValoresRefTexto(prev => ({ ...prev, [index]: raw }));
                                      }}
                                      onBlur={(e) => {
                                        // Ao sair do campo, converte para número e limpa o estado intermediário
                                        const raw = e.target.value;
                                        alterarProduto(index, 'valor_referencia', raw);
                                        setValoresRefTexto(prev => {
                                          const next = { ...prev };
                                          delete next[index];
                                          return next;
                                        });
                                      }}
                                      required
                                    />
                                  </div>
                                </td>
                                <td className="text-end">
                                  {formatCurrency(subtotal)}
                                </td>
                                <td className="text-center">
                                  <button
                                    type="button"
                                    className="btn btn-sm btn-danger"
                                    onClick={() => removerProduto(index)}
                                    title="Remover produto"
                                  >
                                    <i className="ti ti-trash"></i>
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot className="table-light">
                          <tr>
                            <td colSpan={6} className="text-end"><strong>VALOR TOTAL:</strong></td>
                            <td colSpan={2} className="text-end">
                              <strong>{formData.bidValue}</strong>
                            </td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Seção de Campos Personalizados */}
          {camposPersonalizados.length > 0 && (
            <div className="col-12 mt-4">
              <h6 className="mb-3">Campos Personalizados</h6>
              <div className="row">
                {camposPersonalizados.map((campo) => (
                  <div key={campo.id} className="col-md-6">
                    <div className="mb-3">
                      {campo.tipo_campo === 'checkbox' ? (
                        renderizarCampoPersonalizado(campo)
                      ) : (
                        <>
                          <label className="col-form-label">
                            {campo.titulo}
                            {campo.obrigatorio == true && <span className="text-danger">*</span>}
                          </label>
                          {campo.descricao && (
                            <small className="form-text text-muted d-block mb-2">
                              {campo.descricao}
                            </small>
                          )}
                          {renderizarCampoPersonalizado(campo)}
                        </>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="d-flex align-items-center justify-content-end mt-4">
          <button
            type="button"
            className="btn btn-light me-2"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
            onClick={onClose}
            disabled={loading}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                {buttonLoadingText}
              </>
            ) : (
              buttonText
            )}
          </button>
        </div>

        {/* Modal de confirmação de contrato - NOVO */}
        <ModalConfirmacaoContrato
          show={showModalContrato}
          onHide={handleCancelModal}
          onConfirm={handleConfirmModal}
          loading={loading}
        />

        {/* Overlay de carregamento - NOVO */}
        {loading && (
          <div className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
            style={{
              backgroundColor: 'rgba(0,0,0,0.5)',
              zIndex: 1050,
              position: 'fixed',
              left: 0,
              top: 0,
              right: 0,
              bottom: 0
            }}>
            <div className="bg-white p-4 rounded shadow d-flex flex-column align-items-center">
              <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }} role="status">
                <span className="visually-hidden">Carregando...</span>
              </div>
              <div className="text-center">
                <p className="mb-1 fw-bold fs-5">Processando...</p>
                {criarContrato && (
                  <p className="mb-0 text-muted">
                    <i className="ti ti-file-invoice me-1"></i>
                    Criando contrato automaticamente
                  </p>
                )}
              </div>
            </div>
          </div>
        )}
      </form>

    </>
  );
};

export default OpportunityForm;